import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckCircle, XCircle, Shield, Key, Globe, Webhook } from 'lucide-react';
import { usePermission } from '@/hooks/use-permission';
import { authFetch } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface CryptoStatus {
  dbs: {
    pgpKeysLoaded: boolean;
    simulateMode: boolean;
    hasPublicKey: boolean;
    hasPrivateKey: boolean;
  };
  uob: {
    simulateMode: boolean;
  };
  gateway: {
    uen: string;
    callbackUrl: string;
  };
}

function StatusIndicator({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        {ok ? (
          <>
            <CheckCircle className="h-4 w-4 text-green-600" />
            <Badge variant="success">Active</Badge>
          </>
        ) : (
          <>
            <XCircle className="h-4 w-4 text-amber-500" />
            <Badge variant="warning">Not configured</Badge>
          </>
        )}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const canView = usePermission('paynow:settings:view');
  const [status, setStatus] = useState<CryptoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authFetch('/api/v1/settings/crypto-status')
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then(setStatus)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (!canView) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
          <p className="mt-1 text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
          <p className="mt-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load settings: {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
        <p className="mt-1 text-sm text-slate-500">Gateway configuration and crypto status</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-slate-600" />
              <CardTitle>DBS (PGP)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <StatusIndicator ok={status?.dbs.hasPublicKey ?? false} label="DBS Public Key" />
            <StatusIndicator ok={status?.dbs.hasPrivateKey ?? false} label="Keppel Private Key" />
            <StatusIndicator ok={status?.dbs.pgpKeysLoaded ?? false} label="PGP Decrypt Ready" />
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-slate-600">Mode</span>
              <Badge variant={status?.dbs.simulateMode ? 'warning' : 'success'}>
                {status?.dbs.simulateMode ? 'Simulate' : 'Live'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5 text-slate-600" />
              <CardTitle>UOB (AES-GCM)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-600">Mode</span>
              <Badge variant={status?.uob.simulateMode ? 'warning' : 'success'}>
                {status?.uob.simulateMode ? 'Simulate' : 'Live'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-slate-600" />
              <CardTitle>Gateway</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <span className="text-sm text-slate-600">UEN</span>
                <span className="font-mono text-sm text-slate-900">{status?.gateway.uen || '—'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Webhook className="h-5 w-5 text-slate-600" />
              <CardTitle>ERP Callback</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <StatusIndicator ok={!!status?.gateway.callbackUrl} label="Callback URL" />
              {status?.gateway.callbackUrl && (
                <p className="break-all rounded-md bg-slate-50 p-2 font-mono text-xs text-slate-600">
                  {status.gateway.callbackUrl}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
